import type { DiaperPayload, FormulaPayload, LocalEvent } from '@/lib/schemas';
import { addDaysToKey, localDayKey, splitByLocalDay, startOfLocalDayFromKey } from '@/lib/time';

export interface DaySummary {
  day: string;
  /** True if any event of any type was recorded that day (else data is missing, not zero). */
  tracked: boolean;
  formulaMl: number;
  feedCount: number;
  sleepMs: number;
  napCount: number;
  sleepSessions: number;
  wet: number;
  dirty: number;
  diaperCount: number;
  /** Mean minutes between consecutive feeds that day (null if <2 feeds). */
  meanFeedIntervalMin: number | null;
}

function emptyDay(day: string): DaySummary {
  return {
    day,
    tracked: false,
    formulaMl: 0,
    feedCount: 0,
    sleepMs: 0,
    napCount: 0,
    sleepSessions: 0,
    wet: 0,
    dirty: 0,
    diaperCount: 0,
    meanFeedIntervalMin: null,
  };
}

/**
 * Per-local-day summaries for the inclusive range [firstDay, lastDay].
 * Sleep durations are split at local midnight (DST-aware); running sleeps
 * count up to `nowMs`.
 */
export function summarizeDays(
  events: LocalEvent[],
  tz: string,
  firstDay: string,
  lastDay: string,
  nowMs = Date.now(),
): DaySummary[] {
  const days = new Map<string, DaySummary>();
  for (let d = firstDay; d <= lastDay; d = addDaysToKey(d, 1)) days.set(d, emptyDay(d));
  const feedTimes = new Map<string, number[]>();

  for (const e of events) {
    if (e.deleted_at) continue;
    const startDay = localDayKey(e.started_ms, tz);
    const sd = days.get(startDay);
    if (sd) sd.tracked = true;
    switch (e.type) {
      case 'formula': {
        if (!sd) break;
        sd.formulaMl += (e.payload as FormulaPayload).amount_ml;
        sd.feedCount += 1;
        const arr = feedTimes.get(startDay) ?? [];
        arr.push(e.started_ms);
        feedTimes.set(startDay, arr);
        break;
      }
      case 'diaper': {
        if (!sd) break;
        const s = (e.payload as DiaperPayload).status;
        sd.diaperCount += 1;
        if (s === 'wet' || s === 'mixed') sd.wet += 1;
        if (s === 'dirty' || s === 'mixed') sd.dirty += 1;
        break;
      }
      case 'sleep': {
        const end = e.ended_ms ?? Math.max(nowMs, e.started_ms);
        if (sd) {
          sd.sleepSessions += 1;
          if ((e.payload as { sleep_kind?: string }).sleep_kind === 'nap') sd.napCount += 1;
        }
        for (const part of splitByLocalDay(e.started_ms, end, tz)) {
          const pd = days.get(part.day);
          if (pd) {
            pd.sleepMs += part.ms;
            pd.tracked = true;
          }
        }
        break;
      }
    }
  }

  for (const [day, times] of feedTimes) {
    if (times.length < 2) continue;
    times.sort((a, b) => a - b);
    const span = times[times.length - 1] - times[0];
    days.get(day)!.meanFeedIntervalMin = span / (times.length - 1) / 60_000;
  }
  return [...days.values()];
}

export interface HomeStats {
  today: DaySummary;
  lastFeed: LocalEvent | null;
  lastDiaper: LocalEvent | null;
  lastSleepEnd: number | null;
  activeSleep: LocalEvent | null;
  /** Previous 7 complete days (excluding today), oldest first. */
  previous7: DaySummary[];
}

export function homeStats(events: LocalEvent[], tz: string, nowMs = Date.now()): HomeStats {
  const todayKey = localDayKey(nowMs, tz);
  const live = events.filter((e) => !e.deleted_at && e.started_ms <= nowMs + 60_000);
  const days = summarizeDays(live, tz, addDaysToKey(todayKey, -7), todayKey, nowMs);
  const byTimeDesc = [...live].sort((a, b) => b.started_ms - a.started_ms);
  const lastFeed = byTimeDesc.find((e) => e.type === 'formula') ?? null;
  const lastDiaper = byTimeDesc.find((e) => e.type === 'diaper') ?? null;
  const activeSleep = byTimeDesc.find((e) => e.type === 'sleep' && e.ended_ms === null) ?? null;
  const lastSleepEnd =
    byTimeDesc
      .filter((e) => e.type === 'sleep' && e.ended_ms !== null)
      .map((e) => e.ended_ms!)
      .sort((a, b) => b - a)[0] ?? null;
  return {
    today: days[days.length - 1],
    lastFeed,
    lastDiaper,
    lastSleepEnd,
    activeSleep,
    previous7: days.slice(0, 7),
  };
}

export function rangeStartMs(todayKey: string, daysBack: number, tz: string): number {
  return startOfLocalDayFromKey(addDaysToKey(todayKey, -daysBack), tz);
}

export function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}
